import { WeeklySchedule } from './types';
import { DAYS_OF_WEEK } from './values';

export type TimePair = {
  start: number; // minutes from midnight
  end: number; // minutes from midnight
};

type DayEvent = {
  weekday: number;
  type: 'open' | 'close';
  value: {
    $date: number; // Milliseconds
  };
};

type DayEventPair = {
  start: DayEvent;
  end: DayEvent;
};

export type TimePairsByWeekday = {
  monday: TimePair[];
  tuesday: TimePair[];
  wednesday: TimePair[];
  thursday: TimePair[];
  friday: TimePair[];
  saturday: TimePair[];
  sunday: TimePair[];
};

const minutesFromMidnight = (date: { $date: number }) => {
  const time = date.$date;
  return Math.floor(time / 60.0 / 1000.0);
};

const compareEvents = (a: DayEvent, b: DayEvent) => {
  if (a.weekday !== b.weekday) {
    return a.weekday - b.weekday;
  }
  if (a.value.$date !== b.value.$date) {
    return a.value.$date - b.value.$date;
  }
  return a.type === 'open' ? -1 : 1;
};

function toPairs(arr: DayEvent[]): DayEventPair[] {
  const ret = [];
  for (let i = 0; i < arr.length - 1; i += 2) {
    ret.push({ start: arr[i], end: arr[i + 1] });
  }
  return ret;
}

function toTimeSlots(arr: DayEventPair[]): TimePairsByWeekday {
  const timeSlots: TimePairsByWeekday = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };
  arr.forEach(({ start, end }) => {
    timeSlots[DAYS_OF_WEEK[start.weekday]].push({
      start: minutesFromMidnight(start.value),
      end: minutesFromMidnight(end.value),
    });
  });
  DAYS_OF_WEEK.forEach(d => {
    timeSlots[d].sort((a, b) => a.start - b.start);
  });
  return timeSlots;
}

const fromWeeklyScheduleToTimePairsByWeekday = (weeklySchedule: WeeklySchedule): TimePairsByWeekday => {
  const sortedOpeningTimes = DAYS_OF_WEEK.flatMap(weekday =>
    weeklySchedule[weekday].map(time => ({ ...time, weekday: DAYS_OF_WEEK.indexOf(weekday) })),
  ).sort(compareEvents);

  if (sortedOpeningTimes.length > 0 && sortedOpeningTimes[0].type === 'close') {
    const close = sortedOpeningTimes.shift() as DayEvent;
    sortedOpeningTimes.push(close);
  }

  // Add missing opens and closes
  let isOpen = false;
  const filledOpeningTimes: DayEvent[] = [];
  sortedOpeningTimes.forEach(event => {
    if (event.type === 'open') {
      if (isOpen) {
        filledOpeningTimes.push({ ...event, type: 'close' });
      }
      isOpen = true;
      filledOpeningTimes.push(event);
      return;
    }

    if (isOpen) {
      filledOpeningTimes.push(event);
      isOpen = false;
    } else {
      // Compatible with old code: extra close gets a open from start of day
      filledOpeningTimes.push({ type: 'open', value: { $date: 0 }, weekday: event.weekday });
      filledOpeningTimes.push(event);
    }
  });

  const pairs = toPairs(filledOpeningTimes);
  const filledPairs = pairs.flatMap(({ start, end }): DayEventPair[] => {
    if (
      start.weekday === end.weekday ||
      ((start.weekday + 1) % 7 === end.weekday && start.value.$date >= end.value.$date)
    ) {
      return [{ start, end }];
    }

    const ret: DayEventPair[] = [];
    ret.push({
      start,
      end: { type: 'close', value: { $date: 86400000 }, weekday: start.weekday },
    });
    for (let i = (start.weekday + 1) % 7; i !== end.weekday; i = (i + 1) % 7) {
      ret.push({
        start: { type: 'open', value: { $date: 0 }, weekday: i },
        end: { type: 'close', value: { $date: 86400000 }, weekday: i },
      });
    }
    ret.push({
      start: { type: 'open', value: { $date: 0 }, weekday: end.weekday },
      end,
    });
    return ret;
  });

  const timeSlots = toTimeSlots(filledPairs);
  return timeSlots;
};

export default fromWeeklyScheduleToTimePairsByWeekday;
